/**
 * Synthetic pathway state fixtures (PROD-572).
 *
 * Carries the canonical lab-basket fixtures and NBA candidate fixtures
 * used by the ADR-001 correction tests. Each fixture is hand-written
 * (not generated) so it doubles as a reviewable spec for the corrected
 * behavior.
 *
 * Two fixtures of interest:
 *   1. `tlp1KhalidLabBasket` - Cardiometabolic Pathway baseline lab basket. TSH appears
 *      pre-selected with `requiresConfirmation: true`. The prior
 *      prototype's auto-include behavior (ADR-001 violation) is replaced.
 *   2. `tlp1EndocrinologyReferralNba` - the NBA candidate that the prior
 *      prototype permanently suppressed after one override. The
 *      corrected behavior reduces confidence and re-buckets priority but
 *      never deletes (HC8 no-over-suppression).
 *
 * Sources of truth:
 *   - master prompt v3.3 §Architecture Constraint correction list
 *   - SPEC §2.2 layer enforcement table
 *   - COMPONENT-CONTRACTS §9 NbaCard NC-9 + NC-10
 *   - PATHWAY-PRD §6.3 OV-4 "Override does not over-suppress"
 *
 * HC5: zero em / en dashes anywhere. Section separators use `-` (hyphen),
 * commas, or periods.
 */
import {
  basketRequiresConfirmation,
  labBasketItemSchema,
  labBasketSchema,
  type LabBasket,
  type LabBasketItem,
} from '../types/lab-basket';
import {
  nbaCandidateSchema,
  priorityForConfidence,
  type NbaCandidate,
} from '../types/nba';

/** Cardiometabolic Pathway patient + tenant context applied to the canonical fixtures. */
const TLP1_PATHWAY_ID = 'tlp1-glp1-v1';
const TLP1_STEP_ID = 'step-1';
const APEX_TENANT_ID = 'apex-cardiometabolic';

/**
 * Cardiometabolic Pathway baseline lab basket for Khalid Al-Mansoori. Each item is parsed
 * through the Zod schema at module load so any drift from the contract
 * fails the build.
 *
 * ADR-001 correction 1 anchor: TSH is pre-selected with
 * `requiresConfirmation: true`. The prior auto-include behavior is gone;
 * the only path to dispatch the TSH order is an explicit clinician
 * confirmation (one click).
 */
export const tlp1KhalidLabBasketItems: LabBasketItem[] = [
  labBasketItemSchema.parse({
    code: 'HBA1C',
    name: 'Hemoglobin A1c',
    preselected: true,
    requiresConfirmation: true,
    confirmedAt: null,
    rationale: 'Required before Cardiometabolic Pathway Step 3 gate. Patient fasting confirmed.',
  }),
  labBasketItemSchema.parse({
    code: 'LIPID',
    name: 'Lipid panel',
    preselected: true,
    requiresConfirmation: true,
    confirmedAt: null,
    rationale: 'Cardiometabolic risk stratification baseline.',
  }),
  labBasketItemSchema.parse({
    code: 'CMP',
    name: 'Comprehensive metabolic panel',
    preselected: true,
    requiresConfirmation: true,
    confirmedAt: null,
    rationale: 'Renal and hepatic baseline before GLP-1 initiation.',
  }),
  labBasketItemSchema.parse({
    code: 'TSH',
    name: 'Thyroid-stimulating hormone',
    preselected: true,
    requiresConfirmation: true,
    confirmedAt: null,
    rationale:
      'Pre-selected per clinic Cardiometabolic Pathway baseline. Requires one-click clinician confirmation per ADR-001.',
  }),
  labBasketItemSchema.parse({
    code: 'EGFR',
    name: 'Estimated glomerular filtration rate',
    preselected: true,
    requiresConfirmation: true,
    confirmedAt: null,
    rationale: 'Renal function baseline; required before GLP-1 dosing.',
  }),
];

export const tlp1KhalidLabBasket: LabBasket = labBasketSchema.parse({
  pathwayId: TLP1_PATHWAY_ID,
  stepId: TLP1_STEP_ID,
  tenantId: APEX_TENANT_ID,
  items: tlp1KhalidLabBasketItems,
});

/**
 * Lookup helper for tests + view consumers. Returns the TSH item from
 * the Cardiometabolic Pathway basket. Throws if it is ever absent (regression guard).
 */
export function getTlp1TshLabItem(): LabBasketItem {
  const tsh = tlp1KhalidLabBasket.items.find((item) => item.code === 'TSH');
  if (!tsh) {
    throw new Error(
      'tlp1KhalidLabBasket missing TSH item; ADR-001 correction 1 regression',
    );
  }
  return tsh;
}

/**
 * Convenience: returns the codes of the items in the basket that still
 * require clinician confirmation. Used by the
 * `lab_basket_confirmation_required` telemetry payload.
 */
export function pendingConfirmationCodes(basket: LabBasket): string[] {
  return basket.items
    .filter((item) => item.requiresConfirmation && item.confirmedAt === null)
    .map((item) => item.code);
}

/**
 * Re-export for the runtime guard used by views; pre-rendering must
 * decide whether to show the "Confirm and Order" CTA.
 */
export { basketRequiresConfirmation };

/**
 * Cardiometabolic Pathway Endocrinology referral NBA candidate. The prior prototype
 * permanently suppressed this NBA for the clinic after a single
 * clinician override. ADR-001 correction 2 + HC8 replace that with
 * confidence reduction; the candidate continues to surface at a lower
 * priority for context-dependent re-evaluation.
 *
 * Confidence is the un-decayed default (0.92, mid-high). After one
 * override the learning loop reduces it to `max(0.20, 0.92 * 0.6) =
 * 0.552` (medium bucket); after two overrides to `0.331` (low bucket);
 * after three or more overrides it floors at `0.20` (low bucket).
 */
export const tlp1EndocrinologyReferralNba: NbaCandidate = nbaCandidateSchema.parse({
  id: 'tlp1-endo-referral',
  pathwayId: TLP1_PATHWAY_ID,
  stepId: TLP1_STEP_ID,
  tenantId: APEX_TENANT_ID,
  title: 'Refer to endocrinology for thyroid workup',
  confidence: 0.92,
  priority: priorityForConfidence(0.92),
  state: 'active',
  overrideCount: 0,
  lastOverrideAt: null,
});

/**
 * Default NBA candidate set surfaced for Cardiometabolic Pathway Step 1. Used by the
 * learning loop tests + the future NbaCard view. Items are typed so the
 * priority bucket always matches the current confidence at module load.
 */
export const tlp1Step1NbaCandidates: NbaCandidate[] = [
  tlp1EndocrinologyReferralNba,
];

/**
 * Helper used by tests to retrieve a fresh deep copy of the canonical
 * Endocrinology referral NBA. Mutations in tests must not bleed into
 * other tests via the shared module reference.
 */
export function makeTlp1EndocrinologyReferralNba(): NbaCandidate {
  return nbaCandidateSchema.parse({
    ...tlp1EndocrinologyReferralNba,
    // Re-derive priority to keep the contract self-consistent if the
    // confidence ever changes via a separate edit.
    priority: priorityForConfidence(tlp1EndocrinologyReferralNba.confidence),
  });
}
